/**
 * ThumbnailFilters Component
 *
 * Filter pills for pitch prediction thumbnails with bidirectional sync.
 * Selecting a filter updates the grid, clicking a thumbnail updates filters.
 *
 * Follows design patterns from BasketballClipping.jsx.
 *
 * @module components/prediction/ThumbnailFilters
 */

import React, { useMemo } from 'react';
import { Filter, X } from 'lucide-react';
import {
  PITCH_TYPE_LABELS,
  CONFIDENCE_LEVEL_COLORS
} from '../../lib/predictionService';

// ═══════════════════════════════════════════════════════════════════════════
// FILTER PILL COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Individual filter pill button
 */
function FilterPill({ label, count, isActive, onClick, color = null }) {
  const baseClasses = 'px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 min-h-[44px]';

  const activeClasses = color
    ? `${color.bg} ${color.text} border ${color.border}`
    : 'bg-orange-500 text-white border border-orange-500';

  const inactiveClasses = 'bg-white/5 text-gray-400 border border-white/[0.08] hover:bg-white/10 hover:text-white';

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}
      aria-pressed={isActive}
    >
      <span>{label}</span>
      {count !== undefined && (
        <span className={`text-xs ${isActive ? 'opacity-80' : 'opacity-60'}`}>
          ({count})
        </span>
      )}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ThumbnailFilters - Filter pills for thumbnail grid
 *
 * @param {Object} props
 * @param {Object} props.filters - Current active filters
 * @param {Function} props.onFiltersChange - Callback when filters change
 * @param {Object} props.filterOptions - Available filter options with counts
 * @param {Function} props.onClearAll - Callback to clear all filters
 */
export default function ThumbnailFilters({
  filters = {},
  onFiltersChange,
  filterOptions = {},
  onClearAll
}) {
  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      (filters.pitchTypes?.length > 0) ||
      (filters.confidenceLevels?.length > 0) ||
      (filters.tellTypes?.length > 0) ||
      filters.tippingOnly
    );
  }, [filters]);

  // Toggle a pitch type filter
  const togglePitchType = (type) => {
    const current = filters.pitchTypes || [];
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type];

    onFiltersChange({ ...filters, pitchTypes: updated });
  };

  // Toggle a confidence level filter
  const toggleConfidenceLevel = (level) => {
    const current = filters.confidenceLevels || [];
    const updated = current.includes(level)
      ? current.filter(l => l !== level)
      : [...current, level];

    onFiltersChange({ ...filters, confidenceLevels: updated });
  };

  // Toggle a tell type filter
  const toggleTellType = (type) => {
    const current = filters.tellTypes || [];
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type];

    onFiltersChange({ ...filters, tellTypes: updated });
  };

  // Toggle tipping only filter
  const toggleTippingOnly = () => {
    onFiltersChange({ ...filters, tippingOnly: !filters.tippingOnly });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <Filter className="w-5 h-5" />
          <span className="font-semibold">Filters</span>
        </div>

        {hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
            Clear all
          </button>
        )}
      </div>

      {/* Pitch Type Filters */}
      {filterOptions.pitchTypes?.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500">
            Pitch Type
          </h4>
          <div className="flex flex-wrap gap-2">
            {filterOptions.pitchTypes.map(({ type, count }) => (
              <FilterPill
                key={type}
                label={PITCH_TYPE_LABELS[type] || type}
                count={count}
                isActive={filters.pitchTypes?.includes(type)}
                onClick={() => togglePitchType(type)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Confidence Level Filters */}
      {filterOptions.confidenceLevels?.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500">
            Confidence Level
          </h4>
          <div className="flex flex-wrap gap-2">
            {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(level => {
              const option = filterOptions.confidenceLevels.find(c => c.level === level);
              if (!option) return null;

              return (
                <FilterPill
                  key={level}
                  label={level}
                  count={option.count}
                  isActive={filters.confidenceLevels?.includes(level)}
                  onClick={() => toggleConfidenceLevel(level)}
                  color={CONFIDENCE_LEVEL_COLORS[level]}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Tell Type Filters */}
      {filterOptions.tellTypes?.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500">
            Tell Type
          </h4>
          <div className="flex flex-wrap gap-2">
            {filterOptions.tellTypes.slice(0, 8).map(({ type, count }) => (
              <FilterPill
                key={type}
                label={type.replace(/_/g, ' ')}
                count={count}
                isActive={filters.tellTypes?.includes(type)}
                onClick={() => toggleTellType(type)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Tipping Only Toggle */}
      <div className="pt-2 border-t border-white/[0.08]">
        <FilterPill
          label="Show tipping only"
          isActive={filters.tippingOnly}
          onClick={toggleTippingOnly}
        />
      </div>
    </div>
  );
}
